import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { Machine } from '../../types/machine';
import { Tool, CreateToolRequest, UpdateToolRequest } from '../../types/inventory';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchTools,
  createTool,
  updateTool,
  deleteTool,
  updateToolStatus,
} from '../../store/slices/inventorySlice';
import { StatusBadge } from '../inventory/StatusBadge';
import { BulkActionsToolbar } from '../inventory/BulkActionsToolbar';

interface MachineToolsTabProps {
  machine: Machine;
}

const statusOptions = [
  { value: 'available', label: 'Verfügbar' },
  { value: 'in_use', label: 'In Benutzung' },
  { value: 'maintenance', label: 'Wartung' },
  { value: 'missing', label: 'Vermisst' },
];

const conditionOptions = [
  { value: 'excellent', label: 'Ausgezeichnet' },
  { value: 'good', label: 'Gut' },
  { value: 'fair', label: 'Akzeptabel' },
  { value: 'poor', label: 'Schlecht' },
];

export const MachineToolsTab: React.FC<MachineToolsTabProps> = ({ machine }) => {
  const dispatch = useAppDispatch();
  const { items: tools, isLoading, isCreating } = useAppSelector((state) => state.inventory.tools);
  const { user } = useAppSelector((state) => state.auth);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Tool | null>(null);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [formData, setFormData] = useState<CreateToolRequest>({
    name: '',
    description: '',
    tool_number: '',
    location: '',
    condition: 'good',
  });

  const canEdit = user?.role === 'admin';
  const machineTools = tools.filter(t => t.machine_id === machine.id);
  const displayedTools = statusFilter === 'all'
    ? machineTools
    : machineTools.filter(t => t.status === statusFilter);

  const availableCount = machineTools.filter(t => t.status === 'available').length;
  const inUseCount = machineTools.filter(t => t.status === 'in_use').length;
  const maintenanceCount = machineTools.filter(t => t.status === 'maintenance').length;

  useEffect(() => {
    dispatch(fetchTools({ machineId: machine.id }));
  }, [dispatch, machine.id]);

  const handleOpenDialog = (item?: Tool) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        name: item.name,
        description: item.description || '',
        tool_number: item.tool_number || '',
        location: item.location || '',
        condition: item.condition,
      });
    } else {
      setSelectedItem(null);
      setFormData({
        name: '',
        description: '',
        tool_number: '',
        location: '',
        condition: 'good',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedItem(null);
  };

  const handleSave = async () => {
    if (selectedItem) {
      await dispatch(updateTool({
        id: selectedItem.id,
        data: formData as UpdateToolRequest,
      }));
    } else {
      await dispatch(createTool({
        machineId: machine.id,
        data: formData,
      }));
    }
    handleCloseDialog();
    dispatch(fetchTools({ machineId: machine.id }));
  };

  const handleDelete = async (id: string) => {
    await dispatch(deleteTool(id));
    setDeleteDialogOpen(false);
    setSelectedItem(null);
    dispatch(fetchTools({ machineId: machine.id }));
  };

  const handleQuickStatusChange = async (toolId: string, newStatus: string) => {
    await dispatch(updateToolStatus({ id: toolId, status: newStatus }));
    dispatch(fetchTools({ machineId: machine.id }));
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
    },
    {
      field: 'tool_number',
      headerName: 'Werkzeugnummer',
      width: 150,
    },
    {
      field: 'location',
      headerName: 'Standort',
      width: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => (
        <StatusBadge type="tool-status" value={params.value} />
      ),
    },
    {
      field: 'condition',
      headerName: 'Zustand',
      width: 150,
      renderCell: (params) => (
        <StatusBadge type="tool-condition" value={params.value} />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Aktionen',
      width: 150,
      getActions: (params) => {
        const actions = [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Bearbeiten"
            onClick={() => handleOpenDialog(params.row as Tool)}
            disabled={!canEdit}
          />,
        ];
        if (canEdit) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Löschen"
              onClick={() => {
                setSelectedItem(params.row as Tool);
                setDeleteDialogOpen(true);
              }}
            />
          );
        }
        return actions;
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Werkzeuge ({displayedTools.length})
        </Typography>
        <Box display="flex" gap={1}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Alle</MenuItem>
              {statusOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            onClick={() => dispatch(fetchTools({ machineId: machine.id }))}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Hinzufügen
            </Button>
          )}
        </Box>
      </Box>

      {/* Status Summary */}
      <Box display="flex" gap={1} mb={2}>
        <Chip label={`Verfügbar: ${availableCount}`} color="success" size="small" />
        <Chip label={`In Benutzung: ${inUseCount}`} color="primary" size="small" />
        <Chip label={`Wartung: ${maintenanceCount}`} color="warning" size="small" />
      </Box>

      {/* Bulk Actions */}
      {selectedRows.length > 0 && canEdit && (
        <BulkActionsToolbar
          selectedCount={selectedRows.length}
          onClear={() => setSelectedRows([])}
          inventoryType="tool"
          statusOptions={statusOptions}
          onBulkUpdateStatus={(status) => {
            selectedRows.forEach(id => {
              handleQuickStatusChange(id as string, status);
            });
            setSelectedRows([]);
          }}
        />
      )}

      {/* Data Grid */}
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={displayedTools}
          columns={columns}
          loading={isLoading}
          checkboxSelection={canEdit}
          disableRowSelectionOnClick
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedItem ? 'Werkzeug bearbeiten' : 'Werkzeug hinzufügen'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Werkzeugnummer"
                value={formData.tool_number}
                onChange={(e) => setFormData({ ...formData, tool_number: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Beschreibung"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Standort"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="z.B. Werkbank 2, Schrank A"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Zustand</InputLabel>
                <Select
                  value={formData.condition}
                  label="Zustand"
                  onChange={(e) => setFormData({ ...formData, condition: e.target.value as Tool['condition'] })}
                >
                  {conditionOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || isCreating}
          >
            {selectedItem ? 'Speichern' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Werkzeug löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie "{selectedItem?.name}" wirklich löschen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={() => selectedItem && handleDelete(selectedItem.id)}
            variant="contained"
            color="error"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};